import { api } from '$shared/api';
import { trackEvent } from '$lib/analytics.svelte';

/**
 * Generate a clean shareable URL (no hash) for social media previews.
 * e.g. /duels/5/result → https://beatduel.com/duels/5/result
 * Nginx routes these to the OG controller which serves HTML with
 * proper og:title, og:description, og:image tags for crawlers.
 */
export function getShareUrl(path: string): string {
  // Strip leading #/ if present
  const clean = path.replace(/^#?\/?/, '');
  return `${window.location.origin}/${clean}`;
}

/**
 * Share a card image using Web Share API with fallbacks.
 * Fetches the PNG from the share-card endpoint, then:
 * 1. Web Share API with file (if supported)
 * 2. Download as <a download>
 * 3. Open in new tab
 */
export async function handleShareCard(
  duelId: number,
  text: string,
  type: 'rank' | 'countdown' = 'rank',
): Promise<void> {
  trackEvent('share', { target_id: duelId, target_type: 'duel', metadata: { type } });
  const url = `/api/v1/duels/${duelId}/share-card?type=${type}`;

  try {
    const blob = await api.getBlob(`/duels/${duelId}/share-card?type=${type}`);
    const file = new File([blob], `beatduel-${duelId}.png`, { type: 'image/png' });

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: 'Beat Duel', text });
      return;
    }

    // Fallback: download
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `beatduel-${duelId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  } catch {
    // Last resort: open in new tab
    window.open(url, '_blank');
  }
}
